import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components';
import { useUser } from '@/hooks/useUser';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import { type ReportEvent, type ReportWithEvents } from '@/types/report';
import { type ReportGroupByMode } from '../constants';

export interface ReportExportContext {
    report: ReportWithEvents;
    viewerUserId?: string;
    viewerDisplayName?: string;
    groupBy: ReportGroupByMode;
    organizationLogoUrl?: string;
    fileNameSuffix: string;
}

export type ReportExportFn = (
    context: ReportExportContext,
) => Promise<boolean> | boolean;

export const SELECTION_FILE_NAME_SUFFIX = '_selection';

interface UseReportExportParams {
    report: ReportWithEvents | undefined;
    groupBy: ReportGroupByMode;
}

interface UseReportExportResult {
    isExporting: boolean;
    handleExport: (
        exportFn: ReportExportFn,
        events: ReportEvent[],
    ) => Promise<boolean>;
}

export function useReportExport({
    report,
    groupBy,
}: UseReportExportParams): UseReportExportResult {
    const { user } = useUser();
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = useCallback(
        async (exportFn: ReportExportFn, events: ReportEvent[]) => {
            if (isExporting) {
                return false;
            }
            if (!report) {
                toast.error(t('detail.export.unavailable'));
                return false;
            }
            if (!events.length) {
                toast.error(t('detail.export.failed'));
                return false;
            }
            const isPartial = events.length !== report.events.length;
            const exportedIdentityIds = new Set(
                events.map((event) => event.identityId),
            );
            const exportReport = {
                ...report,
                events,
                identities: report.identities.filter((identity) =>
                    exportedIdentityIds.has(identity.id),
                ),
            };
            setIsExporting(true);
            try {
                const success = await exportFn({
                    report: exportReport,
                    viewerUserId: user?.id,
                    viewerDisplayName: user?.name,
                    groupBy,
                    organizationLogoUrl: EngineService.getOrganizationLogoUrl(),
                    fileNameSuffix: isPartial ? SELECTION_FILE_NAME_SUFFIX : '',
                });
                if (!success) {
                    toast.error(t('detail.export.failed'));
                }
                return success;
            } finally {
                setIsExporting(false);
            }
        },
        [isExporting, report, user?.id, user?.name, groupBy, t],
    );

    return { isExporting, handleExport };
}
